import React, { useMemo, useState, useCallback } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { Box, Typography, Stack, Divider, Checkbox, FormControlLabel } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TrendingUp } from '@mui/icons-material';

// Order for stage labels
const STAGE_ORDER = ['pre', 'mid', 'post'];
const STAGE_LABEL = { pre: 'Pre', mid: 'Mid', post: 'Post' };

// Build a stable label like "Cycle 1 - Pre"
const labelFor = (cycle, stage) => `Cycle ${cycle} - ${STAGE_LABEL[stage]}`;

const ProgramGrowthChart = ({ data = [] }) => {
  // Visible programs (program_id → boolean)
  const [visible, setVisible] = useState(() => ({}));

  const {
    xLabels,                 // array of all labels in order
    series,                  // array: { id, label, program_id, data: [] }
    programs                 // map: program_id -> program_name
  } = useMemo(() => {
    if (!data || data.length === 0) {
      return { xLabels: [], series: [], programs: {} };
    }

    // 1) Group by location+program, and collect cycles/stages
    const groups = new Map(); // key: `${location_id}:${program_id}`
    const programsMap = {};   // program_id -> program_name
    const cycles = new Set();

    for (const row of data) {
      const gkey = `${row.location_id}:${row.program_id}`;
      if (!groups.has(gkey)) {
        groups.set(gkey, {
          program_id: row.program_id,
          program_name: row.program_name,
          location_name: row.location_name,
          points: [] // {cycle, stage, score}
        });
      }
      const stage = row.pre ? 'pre' : row.mid ? 'mid' : 'post';
      groups.get(gkey).points.push({
        cycle: row.cycle,
        stage,
        score: row.average_score ?? null,
      });
      programsMap[row.program_id] = row.program_name;
      cycles.add(row.cycle);
    }

    // 2) Build global x-axis in stable order
    const sortedCycles = Array.from(cycles).sort((a, b) => a - b);
    const xLabels = [];
    for (const c of sortedCycles) {
      for (const st of STAGE_ORDER) xLabels.push(labelFor(c, st));
    }

    // 3) For each group, align series to xLabels
    const series = [];
    for (const g of groups.values()) {
      // Make an index for quick lookup
      const idx = new Map(); // key: `${cycle}:${stage}` -> score
      for (const p of g.points) idx.set(`${p.cycle}:${p.stage}`, p.score);

      const aligned = xLabels.map(lbl => {
        // parse "Cycle N - Stage"
        const [_, cycStr, stageStr] = lbl.match(/^Cycle (\d+) - (Pre|Mid|Post)$/) || [];
        const cycle = Number(cycStr);
        const stage = stageStr?.toLowerCase();
        const val = idx.get(`${cycle}:${stage}`);
        return val ?? null; // null breaks the line when missing
      });

      series.push({
        id: `${g.program_id}:${g.location_name}`,
        label: `${g.program_name} @ ${g.location_name}`,
        program_id: g.program_id,
        data: aligned,
        showMark: true,
      });
    }

    return { xLabels, series, programs: programsMap };
  }, [data]);

  // Initialize visibility (first render / when programs change)
  React.useEffect(() => {
    if (!programs) return;
    setVisible(prev => {
      const next = { ...prev };
      for (const pid of Object.keys(programs)) {
        if (next[pid] === undefined) next[pid] = true; // default visible
      }
      return next;
    });
  }, [programs]);

  const toggleProgram = useCallback((pid) => {
    setVisible(v => ({ ...v, [pid]: !v[pid] }));
  }, []);

  const filteredSeries = useMemo( () => series.filter(s => visible[s.program_id]), [series, visible] );

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight="bold">Program growth</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              Growth by program (pre / mid / post)
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <LineChart
        xAxis={[{ scaleType: 'point', data: xLabels, label: 'Assessment Stage', tickLabelStyle: {
          textAnchor: 'middle',
          fontSize: 11
        },}]}
        yAxis={[{ label: 'Average Score (Norm)' }]}
        series={filteredSeries}
        slotProps={{
          legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' }},
        }}
        margin={{ top: 40, right: 20, left: 50, bottom: 20 }}
        height={310}
        sx={(theme) => ({
          // Softer grid background on hover of legend (optional polish)
          '& .MuiChartsLegend-root': {
            background: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(2px)',
            borderRadius: 6,
            px: 1,
          },
        })}
      />
      {/* Program toggles */}
      <Box sx={{ mt: 1 }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
          {Object.entries(programs).map(([pid, name]) => (
            <FormControlLabel
              key={pid}
              control={
                <Checkbox
                  size="small"
                  checked={!!visible[pid]}
                  onChange={() => toggleProgram(pid)}
                  color="secondary"
                />
              }
              label={<Typography variant="caption">{name}</Typography>}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default ProgramGrowthChart;